import { useState } from 'react';
import { platformLabel, PLATFORM_OPTIONS } from '../../lib/platforms';
import type { ContentGoalsRead, PlannedPostRead } from '../../types/api';
import { daysInMonth, monthName } from './calendarDates';

/**
 * Weeks a month is worth for cadence purposes. Rounded, not exact: a target of
 * "three a week" over 31 days is a rhythm, not 13.28 posts.
 */
function weeksIn(month: string): number {
  return Math.max(Math.round(daysInMonth(month) / 7), 1);
}

/** One platform's rhythm this month: what was asked for, what is on the calendar. */
interface CadenceLine {
  platform: string;
  label: string;
  weekly: number;
  target: number;
  planned: number;
}

function cadenceLines(
  goals: ContentGoalsRead,
  month: string,
  monthPosts: readonly PlannedPostRead[],
): CadenceLine[] {
  const weeks = weeksIn(month);
  const known = PLATFORM_OPTIONS.map((option) => option.value as string);
  const extras = Object.keys(goals.cadence_targets).filter((key) => !known.includes(key));

  return [...known, ...extras]
    .map((platform) => {
      const weekly = goals.cadence_targets[platform] ?? 0;
      return {
        platform,
        label: platformLabel(platform),
        weekly,
        target: weekly * weeks,
        planned: monthPosts.filter((post) => post.platform === platform).length,
      };
    })
    .filter((line) => line.weekly > 0);
}

function Meter({ line }: { line: CadenceLine }) {
  const ratio = line.target === 0 ? 0 : Math.min(line.planned / line.target, 1);
  const met = line.target > 0 && line.planned >= line.target;

  return (
    <span className={met ? 'goals-meter goals-meter--met' : 'goals-meter'}>
      <span className="goals-meter__label">{line.label}</span>
      <span className="goals-meter__track">
        <span className="goals-meter__fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
      </span>
      <span className="goals-meter__count">
        {line.planned}/{line.target}
      </span>
    </span>
  );
}

interface GoalsStripProps {
  month: string;
  goals: ContentGoalsRead | null;
  isLoading: boolean;
  error: string | null;
  /** Planned posts falling inside the visible month. */
  monthPosts: readonly PlannedPostRead[];
  /** Opens the goals form — for a first set or an edit. */
  onEdit: () => void;
}

/**
 * One line under the header: what the month is for, and whether the plan on
 * screen adds up to it.
 *
 * Advisory throughout. A meter short of its target is a fact about the calendar,
 * not a warning, and nothing on this strip gates anything.
 */
export function GoalsStrip({
  month,
  goals,
  isLoading,
  error,
  monthPosts,
  onEdit,
}: GoalsStripProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return (
      <p className="goals-strip goals-strip--quiet" role="status">
        Reading this month’s goals
      </p>
    );
  }

  if (goals === null) {
    return (
      <div className="goals-strip goals-strip--invite">
        <p className="goals-strip__invite-text">
          {error ?? `No goals on ${monthName(month)} yet. Say what the month is for.`}
        </p>
        <button className="button" type="button" onClick={onEdit}>
          Set goals for {monthName(month)}
        </button>
      </div>
    );
  }

  // Left unmemoised on purpose — the React Compiler owns memoisation here.
  const lines = cadenceLines(goals, month, monthPosts);

  return (
    <section className="goals-strip" aria-label={`Goals for ${monthName(month)}`}>
      <div className="goals-strip__row">
        <p className="goals-strip__focus">{goals.focus ?? 'No focus set for this month.'}</p>

        <div className="goals-strip__meters">
          {lines.map((line) => (
            <Meter key={line.platform} line={line} />
          ))}
        </div>

        <div className="goals-strip__metrics">
          {goals.metric_targets.map((metric) => (
            <span className="goals-chip" key={metric.label}>
              <span className="goals-chip__label">{metric.label}</span>
              <span className="goals-chip__value">
                {metric.current ?? 0}/{metric.target}
              </span>
            </span>
          ))}
        </div>

        <button
          className="button button--ghost goals-strip__toggle"
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {isOpen ? 'Hide detail' : 'Detail'}
        </button>
      </div>

      {isOpen ? (
        <div className="goals-strip__detail">
          {goals.focus ? <p className="goals-strip__focus-full">{goals.focus}</p> : null}

          {lines.length > 0 ? (
            <ul className="goals-strip__lines">
              {lines.map((line) => (
                <li className="goals-strip__line" key={line.platform}>
                  <span className="goals-strip__line-label">{line.label}</span>
                  <span className="goals-strip__line-value">
                    {line.planned} planned against {line.target} · {line.weekly} a week
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="goals-strip__note">No posting rhythm set for this month.</p>
          )}

          {error ? (
            <p className="form__error" role="alert">
              {error}
            </p>
          ) : null}

          <button className="button" type="button" onClick={onEdit}>
            Edit goals
          </button>
        </div>
      ) : null}
    </section>
  );
}
